import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';

/**
 * Region + inflight state lives on the axios instance so any React context
 * or component can drive it without importing React into this file.
 * A tiny event emitter lets subscribers watch the inflight count in real time.
 */
type Listener = (count: number) => void;
const listeners = new Set<Listener>();
let inflight = 0;
let currentRegion: 'CA' | 'US' | null = null;

function notify(): void {
  for (const l of listeners) l(inflight);
}

export function setRegion(region: 'CA' | 'US' | null): void {
  currentRegion = region;
}

export function getRegion(): 'CA' | 'US' | null {
  return currentRegion;
}

export function getInflightCount(): number {
  return inflight;
}

export function subscribeInflight(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/api/v3',
  withCredentials: true,
  timeout: 30_000,
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
    if (currentRegion) config.headers.set('X-Region', currentRegion);
    inflight++;
    notify();
    return config;
  },
  (err) => {
    inflight = Math.max(0, inflight - 1);
    notify();
    return Promise.reject(err);
  },
);

api.interceptors.response.use(
  (res) => {
    inflight = Math.max(0, inflight - 1);
    notify();
    return res;
  },
  (err: AxiosError) => {
    inflight = Math.max(0, inflight - 1);
    notify();
    // Only redirect on 401 for authenticated requests. Login/forgot/reset intentionally
    // hit /auth without a token, so a 401 there is user-facing, not a session expiry.
    if (err.response?.status === 401 && localStorage.getItem('authToken')) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  },
);

export default api;
